import {FC, useEffect} from 'react'
import clsx from 'clsx'
import {useLocation} from 'react-router'
import {WithChildren} from '_metronic/helpers'
import {checkIsActive} from 'utility/checkvalidUrl'
import {useSidebarGroups} from './SidebarGroups'

type Props = {
  /** Stable key the fold state is remembered under (the group's feature code). */
  id: string
  title: string
  fontIcon?: string
  /**
   * The urls of the pages shown in this group. A menu group from sso has no route of its own, so
   * the group is "active" when the current page is one of its children.
   */
  childUrls: string[]
  /** Shown open until the viewer folds it. Every other group starts folded. */
  defaultOpen?: boolean
}

const SidebarMenuItemWithSub: FC<Props & WithChildren> = ({
  children,
  id,
  title,
  fontIcon,
  childUrls,
  defaultOpen = false,
}) => {
  const {pathname} = useLocation()
  const {isOpen, setOpen, reveal} = useSidebarGroups()
  const isActive = childUrls.some((url) => checkIsActive(pathname, url))
  const expanded = isOpen(id, defaultOpen)

  // Arriving on a page inside a folded group unfolds it, so the current page is never hidden.
  // It fires on arrival only, so the group can still be folded while one of its pages is open.
  useEffect(() => {
    if (isActive) reveal(id)
  }, [isActive, reveal, id])

  // No data-kt-menu-trigger: the open/closed state is React's (SidebarGroups), not Metronic's.
  return (
    <div className={clsx('menu-item menu-accordion', {here: isActive, show: expanded})}>
      <span
        className='menu-link'
        role='button'
        aria-expanded={expanded}
        onClick={() => setOpen(id, !expanded)}
      >
        {fontIcon && (
          <span className='menu-icon'>
            <i className={clsx('bi fs-3', fontIcon)}></i>
          </span>
        )}
        <span className='menu-title'>{title}</span>
        <span className='menu-arrow'></span>
      </span>
      <div className='menu-sub menu-sub-accordion'>{children}</div>
    </div>
  )
}

export {SidebarMenuItemWithSub}
